import { useEffect, useRef, useState } from "react";
import { useBlockNoteEditor, useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import { editorTheme } from "./editorTheme";
import { useGetMarkdown } from "@/stores/ProposalStore";

import { useInitEditor } from "./useInitEditor";
import { AnimatedShinyText } from "@/components/ui/animated-shiny-text";
import { Check, Sparkles } from "lucide-react";
import { getProposalMarkdown } from "@/services/apiProposal";
import { Button } from "@/components/ui/button";
import { useGetStreamingStatus } from "./useGetStreamingStatus";
import { useQueryClient } from "@tanstack/react-query";

export function StatusBar({ proposal_id, searchType }) {
  const queryClient = useQueryClient();
  const editor = useBlockNoteEditor();
  const { mutate } = useInitEditor(proposal_id, searchType);
  // Ping streaming status only when streaming status is optimistically set
  const { data: streamingStatus, refetch } = useGetStreamingStatus(
    proposal_id,
    searchType
  );
  const populateWithBackendMarkdown = async () => {
    const markdown = await getProposalMarkdown(proposal_id, searchType);
    const blocks = await editor.tryParseMarkdownToBlocks(markdown);
    editor.replaceBlocks(editor.document, blocks);
    mutate({ blocks: editor.document });
  };
  useEffect(() => {
    let interval;

    // when optimistic is not defined we are not streaming to the frontend but the backend is still working so we ping it to know when it is done
    if (!streamingStatus?.optimistic) {
      // Poll streaming status every 5 seconds
      interval = setInterval(() => {
        refetch();
      }, 5000);
    }

    // don't use streamed data, fetch data from backend and used it.
    if (streamingStatus?.status === "completed") {
      queryClient.invalidateQueries(["editorContent", proposal_id, searchType]);
      clearInterval(interval);
      populateWithBackendMarkdown(editor.document);
    }
    return () => clearInterval(interval);
  }, [streamingStatus, refetch]);

  return (
    <div className="flex justify-center">
      {streamingStatus?.status === "streaming" ? (
        <div
          className={`fixed top-4 rounded-full inline-flex justify-between items-center px-6 py-2 z-[2] bg-primary`}
        >
          {/* <button onClick={populateWithBackendMarkdown}>Populate</button> */}
          <AnimatedShinyText>
            <div className="flex items-center gap-2">
              <Sparkles className="animate-pulse w-5 h-5" />
              Generating proposal. You will be notified by email when it is
              ready
            </div>
          </AnimatedShinyText>
        </div>
      ) : (
        <div
          className={`fixed top-4 rounded-full inline-flex justify-between items-center px-6 py-2 z-[2] bg-success`}
        >
          <div className="flex items-center gap-2">
            <Check className="animate-pulse w-4 h-4" />
            <span>Proposal is ready</span>
            <Button
              size="sm"
              variant="link"
              onClick={() => {
                populateWithBackendMarkdown(editor.document);
              }}
              className="text-success-foreground"
            >
              Edit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StreamingEditor({ streamingStatus, proposal_id, searchType }) {
  const isStreaming = streamingStatus?.status === "streaming";
  const markdown = useGetMarkdown(searchType, proposal_id);

  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const previousScrollTop = useRef(0);
  const editorContainerRef = useRef(null);

  // Creates a new editor instance.
  const editor = useCreateBlockNote({});

  useEffect(() => {
    if (editor && markdown) {
      const getBlocks = async () => {
        const blocks = await editor.tryParseMarkdownToBlocks(markdown);
        editor.replaceBlocks(editor.document, blocks);

        return blocks;
      };
      getBlocks();
    }
  }, [editor, markdown]);

  useEffect(() => {
    const handleScroll = () => {
      if (window) {
        const { scrollY } = window;
        if (scrollY > 500 && scrollY < previousScrollTop.current) {
          setIsUserScrolling(true);
        }

        previousScrollTop.current = scrollY;
      }
    };
    if (window) {
      window.addEventListener("scroll", handleScroll);
      return () => {
        if (window) {
          window.removeEventListener("scroll", handleScroll);
        }
      };
    }
  }, []);

  if (editorContainerRef.current && isStreaming) {
    if (!isUserScrolling) {
      editorContainerRef.current.scrollIntoView({
        block: "end",
      });
    }
  }

  return (
    <>
      <div
        className={`relative shadow-md min-h-screen bg-[var(--gray-50)] border-gray-200 rounded-md`}
        ref={editorContainerRef}
      >
        <BlockNoteView
          editor={editor}
          theme={editorTheme}
          editable={false}
          formattingToolbar={false}
          className="pt-4"
        >
          <StatusBar proposal_id={proposal_id} searchType={searchType} />
        </BlockNoteView>
        {!streamingStatus?.optimistic && (
          <div className="my-4 ml-8">
            <span className="text-muted-foreground bg-muted rounded-md px-4 py-2">
              Streaming interrupted. The proposal is still being generated in
              the background...
            </span>
          </div>
        )}
      </div>
    </>
  );
}

export default StreamingEditor;
